import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./StudentDashboard.css";
import logo from "./banasthali-logo.jpg";
import ChatBot from "./ChatBot";
import { RefreshCw, Trophy, Medal, Award, Info, Image as ImageIcon, Download, Trash2 } from "lucide-react";
import Certificate from "./components/Certificate";

import config from "./config";

const API = config.API_BASE_URL;
const AI_API = config.AI_BASE_URL;
const CATEGORIES = ["Technical", "Cultural", "Workshop", "Hackathon", "Seminar", "Sports", "Entrepreneurship"];

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [searchQuery, setSearchQuery] = useState("");
  const [eventTabSearch, setEventTabSearch] = useState("");
  const [searchResults, setSearchResults] = useState({ events: [], clubs: [] });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [popupAnnouncement, setPopupAnnouncement] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [isRefreshingAI, setIsRefreshingAI] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editProfileData, setEditProfileData] = useState({
    fullName: "",
    department: "",
    year: "",
    bio: "",
    interests: [],
    profileImage: ""
  });

  const [studentResults, setStudentResults] = useState([]);
  const [showCert, setShowCert] = useState(false);
  const [certData, setCertData] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [moments, setMoments] = useState([]);
  const [momentsLoading, setMomentsLoading] = useState(false);
  const [certLoading, setCertLoading] = useState(false);

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditProfileData(prev => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAuthError = useCallback((err) => {
    console.error("Auth Error:", err);
    if (err.response?.status === 403 || err.response?.status === 401) {
      localStorage.clear();
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        // Fetch dashboard data — set loading to false as soon as it arrives
        axios.get(`${API}/api/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          setData(res.data);
          setLoading(false); // Clear loading immediately when data is ready
        }).catch(err => {
          handleAuthError(err);
          setErrorMessage("Failed to load dashboard data.");
          setLoading(false);
        });

        // Safety net: forcibly clear loading after 6s even if fetch is still pending
        const safetyTimer = setTimeout(() => setLoading(false), 6000);

        // Fetch Notifications in background (non-blocking)
        axios.get(`${API}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(notifRes => {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const validNotifs = (notifRes.data.notifications || []).filter(n => new Date(n.createdAt) >= thirtyDaysAgo);
          setNotifications(validNotifs);
          setUnreadNotifications(notifRes.data.unreadCount);
          const latestUnread = validNotifs.find(n => !n.read && !sessionStorage.getItem(`seen_notif_${n._id}`));
          if (latestUnread) setPopupAnnouncement(latestUnread);
        }).catch(err => console.error("Notification fetch failed:", err));

        return () => clearTimeout(safetyTimer);

      } catch (error) {
        handleAuthError(error);
        setErrorMessage("Connection failed");
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [navigate, handleAuthError]);

  const fetchResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/dashboard/student-results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentResults(res.data);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setResultsLoading(false);
    }
  }, [handleAuthError]);

  const fetchMoments = useCallback(async () => {
    setMomentsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/dashboard/moments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMoments(res.data);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setMomentsLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (activeTab === "results" && studentResults.length === 0) {
      fetchResults();
    }
  }, [activeTab, studentResults.length, fetchResults]);

  useEffect(() => {
    if (activeTab === "moments") {
      fetchMoments();
    }
  }, [activeTab, fetchMoments]);

  const handlePreviewCertificate = async (eventId) => {
    if (!eventId) {
      alert("Invalid Event reference.");
      return;
    }
    setCertLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/dashboard/certificate/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If a custom certificate was uploaded by the club leader, download it instead
      if (res.data.manualUrl) {
         const link = document.createElement('a');
         link.href = res.data.manualUrl;
         const fileName = `Certificate_${res.data.eventName}_${res.data.userName.replace(/\s+/g, '_')}`;
         link.setAttribute('download', fileName);
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         return;
      }

      // If it's a winner but no manual certificate is found, don't show the old digital one
      if (res.data.type === "Winner") {
         alert("Your official custom certificate has been published by the club. If the download didn't start, please try re-publishing from the Club Dashboard to ensure the file is properly anchored.");
         return;
      }

      // Participation certificates (Non-winners) still show the general preview modal for now,
      // or we can hide them too if requested. 
      setCertData(res.data);
      setShowCert(true);
    } catch (err) {
      handleAuthError(err);
      alert(err.response?.data?.error || "Certificate calculation failed. Ensure the event has concluded.");
    } finally {
      setCertLoading(false);
    }
  };

  useEffect(() => {
    const fetchAIRecommendations = async () => {
      const studentId = data?.user?._id || data?.user?.id;
      if (!studentId) return;
      try {
        // 8-second timeout: if AI server is slow/down, don't block the dashboard
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        const res = await axios.get(`${AI_API}/recommendations/${studentId}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        setAiRecommendations(res.data);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          console.error("AI Recommendations error:", err);
        }
        // Silently fail — AI is non-critical, dashboard should still work
      }
    };
    if (data?.user?._id) fetchAIRecommendations();
  }, [data?.user?._id, data?.user?.id]);

  const handleRefreshAI = async () => {
    const studentId = data?.user?._id || data?.user?.id;
    if (!studentId) return;
    setIsRefreshingAI(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      const res = await axios.get(`${AI_API}/recommendations/${studentId}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      setAiRecommendations(res.data);
    } catch (err) {
      console.error("AI Recommendations refresh error:", err);
    } finally {
      setTimeout(() => setIsRefreshingAI(false), 500);
    }
  };

  const logActivity = async (actionType, itemId, itemType) => {
    if (data?.user?._id) {
      try {
        await axios.post(`${AI_API}/log-activity?studentId=${data.user._id}&action_type=${actionType}&item_id=${itemId}&item_type=${itemType}`);
      } catch (err) {
        console.error("Error logging activity:", err);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length > 2) {
        try {
          const token = localStorage.getItem("token");
          const res = await axios.get(`${API}/api/search?q=${searchQuery}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSearchResults(res.data);
        } catch (err) {
          console.error("Search error:", err);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleClubClick = (e, clubName) => {
    e.stopPropagation();
    if (clubName && clubName !== "Independent Event") {
      setSelectedCategories([]); // Clear category filters to show the specific club cluster
      setActiveTab("events");
      setEventTabSearch(clubName);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderDashboard = () => {
    if (!data) return null;
    const { user, stats, upcomingEvents } = data;

    return (
      <>
        <section className="welcome-card">
          <div className="welcome-text">
            <h1>Welcome back, {user.fullName?.split(" ")[0] || "Student"}! 👋</h1>
            <p>You have registered for {stats.registeredEventsCount} upcoming event(s). Keep up the activity!</p>
          </div>
        </section>

        <section className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: "#eef2ff" }}>🚀</div>
            <div className="stat-value">{stats.joinedClubsCount}</div>
            <div className="stat-label">Joined Clubs</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: "#ecfdf5" }}>📅</div>
            <div className="stat-value">{stats.upcomingEventsCount}</div>
            <div className="stat-label">Available Campus Events</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: "#fff7ed" }}>⭐</div>
            <div className="stat-value">{stats.points}</div>
            <div className="stat-label">Activity Points</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: "#fdf2f8" }}>✅</div>
            <div className="stat-value">{stats.registeredEventsCount}</div>
            <div className="stat-label">Registered Events</div>
          </div>
        </section>

        <div className="dashboard-sections-grid">
          {/* AI Recommended Section */}
          <section className="section-card">
            <div className="section-header">
              <h3 className="section-title">✨ Recommended for You (AI) <RefreshCw size={14} className={isRefreshingAI ? "spin" : ""} style={{ marginLeft: '10px', cursor: 'pointer', color: '#6366f1' }} onClick={handleRefreshAI} /></h3>
              <button className="text-btn" onClick={() => setActiveTab("events")}>Full List</button>
            </div>
            <div className="event-list">
              {aiRecommendations?.recommended_events?.length > 0 ? (
                aiRecommendations.recommended_events.map((event, index) => (
                  <div className="event-item-modern" key={event._id || event.name} onClick={() => { logActivity("click", event._id || event.name, "event"); navigate(`/viewpost/${event._id || event.name}`); }}>
                    <div className="event-info">
                       <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         {index + 1}. {event.name} 
                         <span className="match-badge">Match Score: {event.priority_score}/10</span>
                       </h4>
                       <div className="tag-row" style={{ display: 'flex', gap: '5px', margin: '4px 0' }}>
                          {(event.tags || []).map(tag => (
                            <span key={tag} className={`tag-mini tag-${tag.toLowerCase()}`} style={{ 
                               fontSize: '10px', 
                               padding: '2px 8px', 
                               borderRadius: '8px', 
                               background: tag === 'Trending' ? '#fee2e2' : (tag === 'Explore' ? '#f0f9ff' : '#e0e7ff'),
                               color: tag === 'Trending' ? '#991b1b' : (tag === 'Explore' ? '#075985' : '#3730a3'),
                               fontWeight: '600'
                             }}>{tag}</span>
                          ))}
                       </div>
                       <div className="ai-reasons-block" style={{ fontSize: '11px', color: '#4338ca', whiteSpace: 'pre-line', lineHeight: '1.4', background: 'rgba(99, 102, 241, 0.05)', padding: '8px', borderRadius: '8px', marginTop: '6px' }}>
                          {event.reason}
                       </div>
                    </div>
                    <button className="register-btn" style={{ height: 'fit-content', alignSelf: 'center' }} onClick={(e) => { e.stopPropagation(); navigate(`/viewpost/${event._id || event.name}`); }}>Details</button>
                  </div>
                ))
              ) : (
                <div className="empty-ai-box">
                  <p>✨ To see AI-powered matches tailored to YOU, try joining some clubs or adding skills to your profile!</p>
                  <button className="text-btn" onClick={() => setActiveTab("profile")}>Enhance My Profile</button>
                </div>
              )}
            </div>
          </section>

          {/* General Upcoming Section */}
          <section className="section-card">
            <div className="section-header">
              <h3 className="section-title">📅 General Upcoming Events</h3>
              <button className="text-btn" onClick={() => setActiveTab("events")}>Full List</button>
            </div>
            <div className="event-list">
              {upcomingEvents?.length > 0 ? (
                upcomingEvents.slice(0, 3).map(event => (
                  <div className="event-item-modern" key={event._id} onClick={() => { logActivity("view", event._id, "event"); navigate(`/viewpost/${event._id}`); }}>
                    <div className="event-info">
                      <h4>{event.title}</h4>
                      <p onClick={(e) => handleClubClick(e, event.club?.name)} style={{ cursor: "pointer", color: "#6366f1", fontWeight: "600" }} className="hover-underline">
                        {event.club?.name || "Independent Event"}
                      </p>
                    </div>
                    <button className="register-btn" onClick={(e) => { e.stopPropagation(); navigate(`/viewpost/${event._id}`); }}>Details</button>
                  </div>
                ))
              ) : (
                <p>No general events found.</p>
              )}
            </div>
          </section>

          <section className="section-card">
            <div className="section-header">
              <h3 className="section-title">🛡️ Discover Clubs <RefreshCw size={14} className={isRefreshingAI ? "spin" : ""} style={{ marginLeft: '10px', cursor: 'pointer', color: '#6366f1' }} onClick={handleRefreshAI} /></h3>
            </div>
            <div className="club-list">
              {aiRecommendations?.recommended_clubs?.length > 0 ? (
                aiRecommendations.recommended_clubs.map(club => (
                  <div className="club-item-modern" key={club.name} onClick={() => logActivity("view", club.name, "club")}>
                    <div className="club-logo-modern">{club.name.charAt(0)}</div>
                    <div className="club-info">
                      <h4>{club.name} <span className="match-badge">Score: {club.priority_score}/10</span></h4>
                      <p style={{ fontSize: '12px', color: '#6366f1', fontWeight: '500' }}>🎯 {club.reason}</p>
                    </div>
                    <button className="join-btn-small">Join</button>
                  </div>
                ))
              ) : (
                <div className="empty-ai-box">
                  <p>No specific club matches found.</p>
                </div>
              )}
            </div>
            {aiRecommendations?.recommended_domains?.length > 0 && (
              <div className="interest-tags" style={{ marginTop: '1.5rem', padding: '10px', background: 'rgba(99, 102, 241, 0.05)', borderRadius: '12px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Based on your interests:</p>
                {aiRecommendations.recommended_domains.map(d => <span key={d} className="tag" onClick={() => logActivity("explore", d, "domain")}>{d}</span>)}
              </div>
            )}
          </section>
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (loading) return (
      <div className="skeleton-container">
        <div className="skeleton-hero"></div>
        <div className="skeleton-stats-grid">
          <div className="skeleton-stat"></div>
          <div className="skeleton-stat"></div>
          <div className="skeleton-stat"></div>
          <div className="skeleton-stat"></div>
        </div>
        <div className="skeleton-content-grid">
          <div className="skeleton-card-large"></div>
          <div className="skeleton-card-small"></div>
        </div>
      </div>
    );
    if (errorMessage) return (
      <div className="error-content">
        <h2>Something went wrong</h2>
        <p>{errorMessage}</p>
        <button onClick={() => window.location.reload()} className="primary-btn">Retry Connection</button>
      </div>
    );

    try {
      switch (activeTab) {
        case "dashboard": return renderDashboard();
        case "clubs": return renderClubs();
        case "events": return renderEvents();
        case "past-events": return renderPastEvents();
        case "explore": return renderExplore();
        case "notifications": return renderNotifications();
        case "results": return renderResults();
        case "moments": return renderMoments();
        case "profile": return renderProfile();
        default: return renderDashboard();
      }
    } catch (err) {
      console.error("Tab Render Error:", err);
      return (
        <div style={{ padding: '40px', textAlign: 'center', background: 'white', borderRadius: '16px' }}>
          <h3>❌ Component Error</h3>
          <p>We encountered a problem rendering this section.</p>
          <button onClick={() => setActiveTab('dashboard')} className="btn-primary">Back to Dashboard</button>
        </div>
      );
    }
  };

  const renderEvents = () => {
    return (
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Campus Events</h2>
          <div className="category-filters-container">
            <div className="category-filters">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  className={`filter-chip ${selectedCategories.includes(cat) ? 'active' : ''}`}
                  onClick={() => toggleCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="tab-search">
              <input
                type="text"
                placeholder="Search all events..."
                value={eventTabSearch}
                onChange={(e) => setEventTabSearch(e.target.value)}
                className="tab-search-input"
              />
            </div>
          </div>
        </div>
        <div className="event-list">
          {data?.upcomingEvents
            .filter(e => selectedCategories.length === 0 || selectedCategories.includes(e.category))
            .filter(e => e.title.toLowerCase().includes(eventTabSearch.toLowerCase()) ||
              e.description?.toLowerCase().includes(eventTabSearch.toLowerCase()) ||
              e.club?.name?.toLowerCase().includes(eventTabSearch.toLowerCase()))
            .map(event => (
              <div className="event-item-modern" key={event._id} onClick={() => navigate(`/viewpost/${event._id}`)}>
                <div className="event-date-box">
                  <div className="month">{new Date(event.date).toLocaleString('default', { month: 'short' })}</div>
                  <div className="day">{new Date(event.date).getDate()}</div>
                </div>
                <div className="event-info">
                  <h4>{event.title} <span className="cat-badge">{event.category}</span></h4>
                  <p onClick={(e) => handleClubClick(e, event.club?.name)} style={{ cursor: "pointer", color: "#6366f1", fontWeight: "600" }} className="hover-underline">
                    {event.club?.name || "Independent Event"}
                  </p>
                </div>
                <button className="register-btn" onClick={(e) => { e.stopPropagation(); navigate(`/viewpost/${event._id}`); }}>View Details</button>
              </div>
            ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (activeTab === "past-events") {
      const fetchPastEvents = async () => {
        try {
          const token = localStorage.getItem("token");
          const res = await axios.get(`${API}/api/events/past`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 365); // Support trailing 12 months
          setPastEvents(res.data.filter(e => new Date(e.date) >= thirtyDaysAgo));
        } catch (err) {
          console.error("Past events error:", err);
        }
      };
      fetchPastEvents();
    }
  }, [activeTab]);

  const renderProfile = () => {
    const { user } = data;

    if (isEditingProfile) {
      return (
        <div className="section-card edit-profile-card">
          <h2 className="section-title">Edit Your Profile</h2>
          <form className="profile-edit-form" onSubmit={handleProfileSubmit}>
            <div className="profile-photo-management">
              <img src={editProfileData.profileImage || user.profileImage} alt="Preview" className="profile-main-img" />
              <div className="photo-actions">
                <input
                  type="file"
                  id="photo-upload"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "profileImage")}
                />
                <label htmlFor="photo-upload" className="outline-btn">Change Photo</label>
                <button
                  type="button"
                  className="danger-btn-text"
                  onClick={() => setEditProfileData(prev => ({ ...prev, profileImage: "" }))}
                >
                  Remove Photo
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={editProfileData.fullName}
                onChange={(e) => setEditProfileData({ ...editProfileData, fullName: e.target.value })}
              />
            </div>
            <div className="form-grid-2">
              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  value={editProfileData.department}
                  onChange={(e) => setEditProfileData({ ...editProfileData, department: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Year</label>
                <select
                  value={editProfileData.year}
                  onChange={(e) => setEditProfileData({ ...editProfileData, year: e.target.value })}
                >
                  <option value="">Select Year</option>
                  <option value="1">1st Year</option>
                  <option value="2">2nd Year</option>
                  <option value="3">3rd Year</option>
                  <option value="4">4th Year</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea
                rows="4"
                value={editProfileData.bio}
                onChange={(e) => setEditProfileData({ ...editProfileData, bio: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Interests (comma separated)</label>
              <input
                type="text"
                value={editProfileData.interests.join(", ")}
                onChange={(e) => setEditProfileData({ ...editProfileData, interests: e.target.value.split(",").map(i => i.trim()) })}
              />
            </div>
            <div className="form-actions">
              <button type="button" className="text-btn" onClick={() => setIsEditingProfile(false)}>Cancel</button>
              <button type="submit" className="primary-btn">Save Changes</button>
            </div>
          </form>
        </div>
      );
    }

    const profileUser = data?.user || {};
    const stats = data?.stats || { points: 0, registeredEventsCount: 0, joinedClubsCount: 0 };
    
    return (
      <div className="profile-pro-container">
        <div className="profile-banner-premium">
          <div className="banner-glow"></div>
        </div>
        <div className="profile-main-card">
          <div className="profile-left-col">
            <div className="profile-avatar-wrapper">
              <img src={profileUser.profileImage || "https://cdn-icons-png.flaticon.com/512/847/847969.png"} alt="Profile" className="profile-avatar-pro" />
              <button className="edit-img-overlay" onClick={() => setIsEditingProfile(true)}>✎ Edit Photo</button>
            </div>
            <div className="profile-stats-mini">
               <div className="mini-stat">
                  <strong>{stats.points}</strong>
                  <span>Pts</span>
               </div>
               <div className="mini-stat">
                  <strong>{stats.joinedClubsCount}</strong>
                  <span>Clubs</span>
               </div>
            </div>
          </div>
          <div className="profile-right-col">
            <div className="profile-id-section">
              <div className="name-group">
                <h2 className="profile-user-name">{profileUser.fullName || "Student Name"}</h2>
                <div className="dept-badge-pro">{profileUser.department} • Year {profileUser.year}</div>
              </div>
              <button className="btn-edit-experience" onClick={() => {
                setEditProfileData({
                  fullName: profileUser.fullName || "",
                  department: profileUser.department || "",
                  year: profileUser.year || "",
                  bio: profileUser.bio || "",
                  interests: profileUser.interests || [],
                  profileImage: profileUser.profileImage || ""
                });
                setIsEditingProfile(true);
              }}>Edit Profile</button>
            </div>
            
            <div className="profile-bio-summary">
                <p>{profileUser.bio || "Enthusiastic student at Banasthali ready to explore new opportunities!"}</p>
            </div>

            <div className="profile-meta-grid">
              <div className="meta-card-pro">
                 <div className="meta-label">Total Engagement</div>
                 <div className="meta-value-pro">{stats.points} Points</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API}/api/user/update-profile`, editProfileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(res.data.message);
      // Ensure the dashboard state is updated immediately with the new user data
      if (res.data.user) {
        setData(prev => ({ ...prev, user: res.data.user }));
      }
      setIsEditingProfile(false);
      window.location.reload(); // Refresh to pull fresh statistics from backend
    } catch (error) {
      alert("Failed to update profile.");
    }
  };

  useEffect(() => {
    if (data?.user?.globalRole === "admin") {
      navigate("/admindashboard");
    }
  }, [data, navigate]);

  const renderClubs = () => {
    const clubs = data?.joinedClubs || [];
    return (
      <div className="section-card" style={{ background: 'transparent', boxShadow: 'none', padding: '0' }}>
        <h2 className="section-title" style={{ marginBottom: '1.5rem', fontSize: '1.4rem' }}>My Joined Clubs</h2>
        <div className="club-grid-premium">
          {clubs.length > 0 ? clubs.map(club => (
            <div key={club._id} className="club-flex-card">
              <div className="club-card-image-wrapper">
                <img src={club.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(club.name)}&background=6366f1&color=fff&bold=true`} alt={club.name} />
              </div>
              <div className="club-card-content">
                <h4 className="club-card-name">{club.name}</h4>
                <div style={{ padding: '4px 10px', background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800', border: '1px solid rgba(99, 102, 241, 0.2)', display: 'inline-block', marginBottom: '8px' }}>
                  {club.memberRole || "Member"}
                </div>
              </div>
              <div className="club-card-actions-vertical">
                <button className="btn-flex-secondary" onClick={() => navigate(`/viewclub?id=${club._id}`)}>
                  View Club Info
                </button>
                <button className="btn-flex-primary" onClick={(e) => handleClubClick(e, club.name)}>
                  See All Events
                </button>
              </div>
            </div>
          )) : (
            <div className="empty-state" style={{ background: 'white', borderRadius: '16px', width: '100%' }}>
              <p>You haven't joined any clubs yet. Explore and join some!</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderExplore = () => {
    const clubs = data?.allClubs || []; // Fixed to use allClubs for the explore tab
    return (
      <div className="section-card">
        <h2 className="section-title">Discover Clubs</h2>
        <p className="subtitle" style={{ marginBottom: '1.5rem' }}>Find your tribe. Hover to see what each club is about.</p>
        <div className="club-grid-premium">
          {clubs.map(club => {
            const isJoined = data?.joinedClubs?.some(jc => {
              const jcId = jc._id || jc.id;
              const clubId = club._id || club.id;
              return jcId && clubId && jcId.toString() === clubId.toString();
            });
            return (
              <div key={club._id} className="club-flex-card">
                <div className="club-card-image-wrapper">
                  <img src={club.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(club.name)}&background=6366f1&color=fff&bold=true`} alt={club.name} />
                </div>
                <div className="club-card-content">
                  <h4 className="club-card-name">{club.name}</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>{club.category}</p>
                  <p style={{ fontSize: '13px', color: '#475569', height: '40px', overflow: 'hidden' }}>{club.description?.substring(0, 60)}...</p>
                </div>
                <div className="club-card-actions-vertical">
                  {isJoined ? (
                    <button className="btn-flex-secondary" disabled style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>
                      Member ✅
                    </button>
                  ) : (
                    <button className="btn-flex-primary" onClick={() => handleJoinClub(club._id)}>
                      Join Club
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {clubs.length === 0 && <p>No clubs available to join at this moment.</p>}
        </div>
      </div>
    );
  };

  const handleJoinClub = async (clubId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/clubs/join/${clubId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Fire and forget logActivity to avoid blocking the UI
      logActivity("join", clubId, "club").catch(e => console.error(e));
      
      alert("Successfully joined club! +10 points.");
      window.location.reload();
    } catch (err) {
      console.error("Join error:", err);
      alert("Failed to join club.");
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const validNotifs = (res.data.notifications || []).filter(n => new Date(n.createdAt) >= thirtyDaysAgo);

      setNotifications(validNotifs);
      setUnreadNotifications(res.data.unreadCount || 0);

      const latestUnread = validNotifs.find(n => !n.read && !sessionStorage.getItem(`seen_notif_${n._id}`));
      if (latestUnread) {
        setPopupAnnouncement(latestUnread);
      }
    } catch (err) {
      console.error("Notifications fetch error:", err);
    }
  };

  const closePopup = () => {
    if (popupAnnouncement) {
      sessionStorage.setItem(`seen_notif_${popupAnnouncement._id}`, "true");
      setPopupAnnouncement(null);
    }
  };

  useEffect(() => {
    if (activeTab === "notifications") {
      fetchNotifications().then(() => {
        const token = localStorage.getItem("token");
        if (token) {
          axios.patch(`${API}/api/notifications/mark-all-read`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => console.error("Failed to mark notifications read", err));
          
          setUnreadNotifications(0);
          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        }
      });
    }
  }, [activeTab]);

  const renderResults = () => {
    return (
      <div className="achievements-section">
         {showCert && <Certificate data={certData} onClose={() => setShowCert(false)} />}
         
         <div className="welcome-card" style={{ marginBottom: '20px' }}>
            <div className="welcome-text">
              <h1>My Achievement Portfolio 🏆</h1>
              <p>View your contest ranks, participation logs, and download verified certificates.</p>
            </div>
         </div>

         <div className="achievement-grid">
            {resultsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', gridColumn: '1/-1' }}><RefreshCw className="spin" /> Loading your achievements...</div>
            ) : studentResults.length === 0 ? (
              <div className="empty-state" style={{ gridColumn: '1/-1' }}>
                <Medal size={60} style={{ opacity: 0.2, marginBottom: '20px' }} />
                <p style={{ color: '#64748b', fontSize: '1.2rem', fontStyle: 'normal' }}>No achievements recorded yet. Participate in campus events to earn badges!</p>
              </div>
            ) : (
              studentResults.map((res) => (
                <div className="achievement-card" key={res.id} style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', minHeight: '220px' }}>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase' }}>{res.club?.name}</span>
                      {res.isWinner ? <Trophy size={20} color="#f59e0b" fill="#f59e0b" /> : <Award size={20} color="#94a3b8" />}
                   </div>
                   <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '12px 0 6px', color: '#1e293b' }}>{res.title}</h3>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                      <span style={{ fontSize: '11px', background: res.isWinner ? '#fef3c7' : '#f1f5f9', color: res.isWinner ? '#92400e' : '#475569', padding: '4px 10px', borderRadius: '20px', fontWeight: 700 }}>
                        {res.isWinner ? `🏆 ${res.position} Place` : '🎓 Participant'}
                      </span>
                      {!res.published && res.isPast && <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '500' }}>• Reviewing results...</span>}
                      {!res.isPast && <span style={{ fontSize: '11px', color: '#6366f1', fontWeight: '500' }}>• Event Ongoing</span>}
                   </div>

                   <div className="card-actions" style={{ marginTop: 'auto', display: 'flex', gap: '10px' }}>
                      <button 
                        className="btn-primary" 
                        style={{ 
                           flex: 1, padding: '10px', fontSize: '0.85rem', borderRadius: '8px', border: 'none', cursor: 'pointer', 
                           background: (res.published) ? '#6366f1' : '#cbd5e1', color: 'white', fontWeight: '600' 
                        }} 
                        onClick={() => res.published ? handlePreviewCertificate(res.id) : alert("Results are still being finalized. The certificate will appear once they are officially published by the club.")}
                        disabled={certLoading}
                      >
                        {certLoading ? <RefreshCw size={14} className="spin" style={{marginRight: '6px'}} /> : <Award size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />}
                        {res.published ? 'Certificate' : 'Locked'}
                      </button>
                      <button 
                        className="btn-secondary" 
                        style={{ padding: '10px', display: 'flex', alignItems: 'center', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer' }} 
                        onClick={() => navigate(`/viewpost/${res.id}`)}
                        title="View Event Details"
                      >
                        <Info size={16} color="#64748b" />
                      </button>
                   </div>
                </div>
              ))
            )}
         </div>
      </div>
    );
  };

  const deleteNotification = async (notifId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/notifications/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n._id !== notifId));
    } catch (err) {
      console.error("Failed to delete notification", err);
    }
  };

  const handleDownloadMoment = (url, caption) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `Moment_${caption || 'Banverse'}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderPastEvents = () => {
    // Current date - 1 year
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const filtered = pastEvents; // Use the state hook we populated

    const grouped = filtered.reduce((acc, event) => {
      const date = new Date(event.date);
      const key = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!acc[key]) acc[key] = [];
      acc[key].push(event);
      return acc;
    }, {});

    return (
      <div className="section-card">
        <h2 className="section-title">Past Events History (Last 1 Year)</h2>
        <p className="subtitle">Only showing events from the previous 12 months for better performance.</p>
        <div className="history-timeline">
          {Object.keys(grouped).length > 0 ? Object.entries(grouped).map(([month, events]) => (
            <div key={month} className="month-group">
              <h3 className="month-label">📅 {month}</h3>
              <div className="month-events">
                {events.map(event => (
                  <div key={event._id} className="past-event-item">
                    <div className={`dot ${event.isRegistered ? 'dot-registered' : 'dot-missed'}`}></div>
                    <div className="past-event-info">
                      <strong>
                        {event.title}
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '500', marginLeft: '8px' }}>
                          ({new Date(event.date).toLocaleDateString()})
                        </span>
                      </strong>
                      <span style={{ color: '#64748b', fontSize: '13px' }}>
                        {event.club?.name || 'Campus Event'} &bull; {event.category}
                      </span>
                    </div>
                    <span
                      className="past-event-badge"
                      style={{
                        marginLeft: 'auto',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        background: event.isRegistered ? '#dcfce7' : '#f1f5f9',
                        color: event.isRegistered ? '#16a34a' : '#94a3b8',
                        border: event.isRegistered ? '1px solid #86efac' : '1px solid #e2e8f0'
                      }}
                    >
                      {event.isRegistered ? '✅ Registered' : '○ Not Registered'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )) : <p className="empty-state">No past events found in the last 1 year.</p>}
        </div>
      </div>
    );
  };

  const renderMoments = () => {
    if (momentsLoading) return <div className="centered"><RefreshCw className="spin" /> Loading memories...</div>;

    return (
      <div className="section-card moments-container">
        <div className="moments-header" style={{ marginBottom: '2rem' }}>
          <h2 className="section-title">Club Moments ✨</h2>
          <p className="section-subtitle">Visual highlights and updates from the clubs you&apos;ve joined.</p>
        </div>

        {moments.length === 0 ? (
          <div className="empty-moments" style={{ textAlign: 'center', padding: '3rem', opacity: 0.6 }}>
             <ImageIcon size={60} style={{ marginBottom: '1rem' }} />
             <p>No formal moments have been shared by your joined clubs yet.</p>
          </div>
        ) : (
          <div className="moments-masonry" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {moments.map((moment, idx) => (
              <div key={idx} className="moment-card-pro" style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #eee' }}>
                <div style={{ position: 'relative' }}>
                  <img src={moment.url} alt={moment.caption} style={{ width: '100%', height: '240px', objectFit: 'cover' }} />
                  <div className="moment-club-tag" style={{ position: 'absolute', top: '12px', left: '12px', background: 'rgba(255,255,255,0.9)', padding: '4px 10px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600 }}>
                    <img src={moment.clubLogo} alt="" style={{ width: '16px', height: '16px', borderRadius: '50%' }} />
                    {moment.clubName}
                  </div>
                  <button 
                    onClick={() => handleDownloadMoment(moment.url, moment.caption)}
                    style={{ position: 'absolute', bottom: '12px', right: '12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}
                    title="Download Image"
                  >
                    <Download size={16} />
                  </button>
                </div>
                <div className="moment-info" style={{ padding: '1rem' }}>
                  <p className="moment-caption" style={{ fontWeight: 500, fontSize: '0.9rem', marginBottom: '0.5rem' }}>{moment.caption}</p>
                  <div className="moment-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#94a3b8' }}>
                    <span className="moment-date">{new Date(moment.uploadedAt).toLocaleDateString()}</span>
                    <span className="moment-cat" style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>{moment.category}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderNotifications = () => {
    return (
      <div className="section-card">
        <h2 className="section-title">Your Notifications</h2>
        <div className="notification-list" style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {notifications.length > 0 ? (
            notifications.map((n, i) => (
              <div key={n._id || i} className={`notif-item ${!n.read ? 'highlight' : ''}`} style={{
                padding: '16px',
                background: n.read ? '#f8fafc' : '#f0f4ff',
                borderRadius: '16px',
                borderLeft: n.read ? 'none' : '4px solid #4f46e5',
                boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <strong>{n.type === 'announcement' ? '📢 Announcement' : '🔔 Notification'}</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>{new Date(n.createdAt).toLocaleString()}</span>
                    <button 
                      onClick={() => deleteNotification(n._id)}
                      style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                      title="Delete Notification"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <p style={{ margin: '8px 0 0 0', color: '#475569', fontSize: '14px', lineHeight: '1.5' }}>{n.message}</p>
              </div>
            ))
          ) : (
            <div className="empty-state" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>🧘</div>
              <p>All caught up! No notifications yet.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };



  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src={logo} alt="Logo" />
          <span>Banverse</span>
        </div>
        <ul className="nav-links">
          <li className={`nav-link ${activeTab === "dashboard" ? "active" : ""}`} onClick={() => setActiveTab("dashboard")}><span>🏠</span> Dashboard</li>
          <li className={`nav-link ${activeTab === "clubs" ? "active" : ""}`} onClick={() => setActiveTab("clubs")}><span>🛡️</span> My Clubs</li>
          <li className={`nav-link ${activeTab === "events" ? "active" : ""}`} onClick={() => setActiveTab("events")}><span>📅</span> Events</li>
          <li className={`nav-link ${activeTab === "past-events" ? "active" : ""}`} onClick={() => setActiveTab("past-events")}><span>🗂️</span> Past Events</li>
          <li className={`nav-link ${activeTab === "results" ? "active" : ""}`} onClick={() => setActiveTab("results")}><span>🏆</span> My Achievements</li>
          <li className={`nav-link ${activeTab === "moments" ? "active" : ""}`} onClick={() => setActiveTab("moments")}><span>✨</span> Club Moments</li>
          <li className={`nav-link ${activeTab === "explore" ? "active" : ""}`} onClick={() => setActiveTab("explore")}><span>🔍</span> Explore Clubs</li>
          <li className={`nav-link ${activeTab === "notifications" ? "active" : ""}`} onClick={() => setActiveTab("notifications")}>
            <span>🔔</span> Notifications
            {unreadNotifications > 0 && <span className="notif-badge">{unreadNotifications}</span>}
          </li>
          <li className={`nav-link ${activeTab === "profile" ? "active" : ""}`} onClick={() => setActiveTab("profile")}><span>👤</span> Profile</li>
          {(data?.user?.isClubLeader || data?.user?.membershipType === "Core Member") && (
            <li className={`nav-link`} onClick={() => { localStorage.setItem("role", "club"); navigate("/clubdashboard"); }} style={{ color: '#6366f1', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '12px' }}>
              <span>👑</span> Club Dashboard
            </li>
          )}
          <li className="nav-link logout" onClick={handleLogout}><span>🚪</span> Logout</li>
        </ul>
      </aside>

      <main className="main-content">
        <header className="top-navbar">
          {activeTab === "profile" ? (
            <h2 className="tab-title">User Profile</h2>
          ) : (
            <>
              <div className="search-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search clubs, events, categories..."
                  className="search-bar-enhanced"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery.length > 2 && (
                  <div className="search-dropdown">
                    {searchResults.events.length > 0 && <h4>Events</h4>}
                    {searchResults.events.map(e => (
                      <div key={e._id} className="search-item" onClick={() => { navigate(`/viewpost/${e._id}`); setSearchQuery(""); }}>
                        <span>📅</span> {e.title}
                      </div>
                    ))}
                    {searchResults.clubs.length > 0 && <h4>Clubs</h4>}
                    {searchResults.clubs.map(c => (
                      <div key={c._id} className="search-item" onClick={() => { navigate(`/viewclub?id=${c._id}`); setSearchQuery(""); }}>
                        <span>🛡️</span> {c.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="user-profile-top" onClick={() => setActiveTab("profile")} style={{ cursor: 'pointer' }}>
                <span className="user-name">{data?.user.fullName || "Loading..."}</span>
                <img src={data?.user.profileImage} alt="User" className="profile-img-small" />
              </div>
            </>
          )}
        </header>

        {loading ? (
           <div className="tab-loading-state" style={{ padding: '6rem 2rem', textAlign: 'center' }}>
              <div className="custom-spinner" style={{ margin: '0 auto', width: '40px', height: '40px', border: '3px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              <p style={{ marginTop: '1.25rem', color: '#64748b', fontWeight: 500 }}>Fetching your Banverse updates...</p>
              <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
              `}</style>
           </div>
        ) : renderContent()}
      </main>
      
      {/* Announcement Popup Toast */}
      {popupAnnouncement && (
        <div style={{
          position: 'fixed', bottom: '20px', right: '20px', background: '#3b82f6', color: 'white',
          padding: '16px 20px', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          zIndex: 9999, maxWidth: '350px', animation: 'slideUpFade 0.4s ease-out'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <strong style={{ fontSize: '15px' }}>📢 New Announcement</strong>
            <button onClick={closePopup} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}>&times;</button>
          </div>
          <p style={{ margin: 0, fontSize: '13.5px', lineHeight: '1.4', opacity: 0.95 }}>{popupAnnouncement.message}</p>
          <button 
            onClick={() => { closePopup(); setActiveTab("notifications"); }} 
            style={{ marginTop: '12px', background: 'white', color: '#3b82f6', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Go to Notifications
          </button>
        </div>
      )}

      <ChatBot />
    </div>
  );
};

export default StudentDashboard;